import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Phone, Zap, Images } from "lucide-react";

export default function LaborerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/laborers/${id}`).then(({ data }) => setData(data)); }, [id]);
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { laborer, reviews } = data;
  const canHire = user?.role === "local";

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-white border border-slate-200/80 rounded-2xl p-6 flex items-start gap-4 flex-wrap">
        <Avatar className="h-20 w-20">
          <AvatarImage src={laborer.avatar_url || undefined} />
          <AvatarFallback className="bg-[#3E2A4E] text-[#D9FF3D] text-2xl font-bold">{laborer.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">{laborer.name}</h1>
          <div className="text-sm text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
            {laborer.city && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {laborer.city}</span>}
            {laborer.review_count > 0 && (
              <span className="inline-flex items-center gap-1"><Star size={13} className="fill-amber-400 text-amber-400" /> {laborer.avg_rating} · {laborer.review_count} reviews</span>
            )}
            {laborer.hourly_rate && <span className="font-mono-money">${laborer.hourly_rate}/hr</span>}
          </div>
          <p className="mt-4 text-slate-700 leading-relaxed">{laborer.bio || "No bio yet."}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(laborer.skills || []).map((s) => <Badge key={s} className="bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-50">{s}</Badge>)}
          </div>
          {canHire && (
            <Button
              onClick={() => navigate(`/post?helper=${laborer.user_id}`)}
              data-testid="hire-now-btn"
              className="mt-5 bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full px-6 btn-primary-glow"
            >
              <Zap size={16} className="mr-1" /> Hire {laborer.name.split(" ")[0]} now
            </Button>
          )}
        </div>
      </div>

      {(laborer.portfolio || []).length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-3 flex items-center gap-2"><Images size={18} /> Portfolio</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {laborer.portfolio.map((url, i) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer" data-testid={`portfolio-view-${i}`} className="block aspect-square rounded-xl overflow-hidden border border-slate-200 card-hover">
                <img src={url} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-heading text-xl font-bold mb-3">Reviews</h2>
        {reviews.length === 0 ? (
          <div className="text-slate-500 text-sm">No reviews yet.</div>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => (
              <div key={r.review_id} className="bg-white border border-slate-200/80 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900">{r.reviewer_name}{r.anonymous && <span className="ml-2 text-xs text-slate-400 font-normal">anonymous</span>}</div>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map((n) => <Star key={n} size={14} className={n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"} />)}
                  </div>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-2">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
